import { MouseEvent, ReactNode } from 'react';

import { Box, Flex, List, ListItem, ListProps } from '@chakra-ui/react';

import { ThirdPartyReview, ThirdPartyReviewContentList } from '~/features/third-party-reviews/model';
import { getReviewLinkLabel } from '~/features/third-party-reviews/utils/get-review-link-label';

import { Link } from './link';
import { Rating } from './rating';
import { SanityImage } from './sanity-image';

export function ThirdPartyReviews({
  reviews,
  customLinkLabel,
  listProps,
  onLinkClick,
  showLinkToReview = true,
}: {
  reviews: ThirdPartyReviewContentList;
  customLinkLabel?: (review: ThirdPartyReview) => ReactNode | string | number;
  listProps?: ListProps;
  onLinkClick?: (e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>, review: ThirdPartyReview) => void;
  showLinkToReview?: boolean;
}) {
  return (
    <Box>
      <List
        display={{ base: 'grid', lg: 'flex' }}
        flexWrap='wrap'
        gridTemplateColumns={{ base: 'repeat(2, 1fr)' }}
        justifyItems={{ base: 'center' }}
        gap={{
          base: '32px 24px',
          lg: '56px',
        }}
        {...listProps}
      >
        {reviews.map((review) => {
          const linkLabel = customLinkLabel?.(review);
          const linkLabelText = getReviewLinkLabel(linkLabel, review);
          return (
            <ListItem key={review.company.name}>
              <Box>
                <Box marginBottom='4px'>
                  <SanityImage alt={review.company.name} image={review.company.lofiLogoDark} />
                </Box>
                <Box>
                  <Flex alignItems='center' gap='8px'>
                    <Rating color='blue.500' rating={review.rating} size='sm' />
                  </Flex>
                  {showLinkToReview && review.url && (
                    <Link
                      alignItems='center'
                      aria-label={`${linkLabelText} ${review.company.name}`}
                      display='inline-flex'
                      gap='4px'
                      href={review.url}
                      whiteSpace='nowrap'
                      isExternal
                      onClick={(e) => onLinkClick?.(e, review)}
                    >
                      {linkLabel}
                    </Link>
                  )}
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
